import { cn } from "@/lib/utils";
import { useActiveOrg } from "@/hooks/use-active-org";
import { useWorkspaceStore } from "@/store/workspace-store";
import { ArrowLeft, LogOut } from "lucide-react";
import { useState } from "react";
import { Link, useLocation, useParams } from "react-router";
import {
  resolveSettingsHref,
  settingsGroups,
  type SettingsGroup,
} from "./settings-nav-config";
import { LogoutConfirmDialog } from "./logout-confirm-dialog";

export function SettingsSidebar() {
  const location = useLocation();
  const { workspaceId: urlWorkspaceId } = useParams<{
    workspaceId: string;
  }>();
  const { activeWorkspaceId } = useWorkspaceStore();
  const { orgId, isOrgAdmin } = useActiveOrg();
  const workspaceId = urlWorkspaceId || activeWorkspaceId || undefined;
  const [logoutOpen, setLogoutOpen] = useState(false);
  // Org-scoped settings (billing) are admin-only — the BE 403s non-admins on
  // every endpoint, so surfacing the link just routes members to a dead page.
  const visibleGroups = isOrgAdmin
    ? settingsGroups
    : settingsGroups.filter((group) => group.title !== "Organization");

  const resolveTo = (path: string) =>
    resolveSettingsHref(path, workspaceId, orgId);

  const isActive = (path: string) => {
    const fullPath = resolveTo(path);
    return (
      location.pathname === fullPath ||
      location.pathname.startsWith(fullPath + "/")
    );
  };

  const linkClass = (active: boolean) =>
    cn(
      "flex items-center gap-2 rounded px-2 py-1.5 text-sm transition-colors",
      active
        ? "bg-foreground/[0.05] font-medium text-foreground"
        : "text-muted-foreground hover:bg-foreground/[0.03] hover:text-foreground"
    );

  const renderSection = (group: SettingsGroup) => (
    <div key={group.title}>
      <div className="px-2 py-1.5 text-[11px] font-medium uppercase tracking-widest text-muted-foreground/50">
        {group.title}
      </div>
      <div className="mt-0.5 space-y-px">
        {group.links.map((link) => (
          <Link
            key={link.path}
            to={resolveTo(link.path)}
            className={linkClass(isActive(link.path))}
          >
            <link.icon className="h-4 w-4 shrink-0" />
            {link.label}
          </Link>
        ))}
      </div>
    </div>
  );

  return (
    <aside className="flex h-full w-56 shrink-0 flex-col border-r border-border bg-sidebar">
      {/* Header */}
      <div className="flex h-14 items-center border-b border-border px-4">
        <Link
          to={workspaceId ? `/${workspaceId}/dashboard` : "/workspaces"}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to app
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-6">
        {visibleGroups.map(renderSection)}
      </nav>

      {/* Log out — session action lives at the foot of settings. */}
      <div className="border-t border-border p-2">
        <button
          type="button"
          onClick={() => setLogoutOpen(true)}
          className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-red-500/10 hover:text-red-600"
        >
          <LogOut className="h-4 w-4 shrink-0" />
          Log out
        </button>
      </div>

      <LogoutConfirmDialog open={logoutOpen} onOpenChange={setLogoutOpen} />
    </aside>
  );
}
